import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Box, Text, Flex, HStack, VStack, Badge, Image, Divider, Spinner, Link,
  Grid, GridItem, IconButton, Tooltip, Textarea, Button, useToast,
} from '@chakra-ui/react';
import {
  FiPhone, FiMail, FiBriefcase, FiCalendar, FiUser, FiMessageSquare, FiEdit2,
} from 'react-icons/fi';
import api from '../api';

const Field = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <Box>
      <Text fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wide" mb={0.5}>{label}</Text>
      <HStack spacing={1.5}>
        {Icon && <Box color="gray.400" flexShrink={0}><Icon size={11} /></Box>}
        <Text fontSize="12px" color="gray.700" wordBreak="break-all">{value}</Text>
      </HStack>
    </Box>
  );
};

export default function ContactQuickViewModal({ contactId, isOpen, onClose, onEdit }) {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const toast = useToast();

  const fetchContact = useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/contacts/${contactId}`);
      setContact(data);
      setNotesDraft(data?.notes || '');
    } catch(e) {}
    setLoading(false);
  }, [contactId]);

  useEffect(() => { if (isOpen && contactId) fetchContact(); }, [isOpen, contactId, fetchContact]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/contacts/${contactId}`, { notes: notesDraft });
      setContact(c => c ? { ...c, notes: notesDraft } : c);
      toast({ title: 'Notes saved', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Could not save notes', status: 'error', duration: 2000 }); }
    finally { setSavingNotes(false); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : null;

  const initials = contact
    ? `${contact.first_name?.[0] || ''}${contact.last_name?.[0] || ''}`.toUpperCase()
    : '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="80vh">
        <ModalCloseButton />
        {onEdit && contact && (
          <Tooltip label="Edit contact" fontSize="xs">
            <IconButton
              icon={<FiEdit2 size={13} />} size="sm" variant="ghost"
              aria-label="Edit contact"
              // Positioned beside the auto-rendered ModalCloseButton (which sits
              // at right=3 / top=2 by default in Chakra). Stacked vertically
              // with the close X to avoid overlap.
              position="absolute" top={2} right={10} zIndex={2}
              onClick={() => { onEdit(contact); onClose(); }}
            />
          </Tooltip>
        )}
        {loading ? (
          <Flex justify="center" align="center" h="200px"><Spinner color="purple.400" /></Flex>
        ) : contact ? (
          <>
            <ModalHeader pb={2}>
              <Flex align="center" gap={3} pr={8}>
                {contact.avatar_url ? (
                  <Image src={`/api/files/${contact.avatar_url}`} w="48px" h="48px"
                    borderRadius="full" objectFit="cover" flexShrink={0} />
                ) : (
                  <Flex w="48px" h="48px" borderRadius="full" bg="purple.100"
                    align="center" justify="center" flexShrink={0}>
                    <Text fontSize="16px" fontWeight="700" color="purple.600">{initials || <FiUser />}</Text>
                  </Flex>
                )}
                <Box>
                  <Text fontSize="16px" fontWeight="800">
                    {contact.first_name} {contact.last_name || ''}
                  </Text>
                  <HStack spacing={2} mt={0.5} flexWrap="wrap">
                    {contact.job_title && (
                      <Text fontSize="12px" color="gray.500">{contact.job_title}</Text>
                    )}
                    {contact.company_name && (
                      <Badge colorScheme="blue" fontSize="9px">{contact.company_name}</Badge>
                    )}
                  </HStack>
                </Box>
              </Flex>
            </ModalHeader>
            <ModalBody pt={0}>
              <VStack spacing={4} align="stretch">
                <Grid templateColumns="1fr 1fr" gap={3}>
                  {contact.email && (
                    <GridItem colSpan={2}>
                      <Box>
                        <Text fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wide" mb={0.5}>Email</Text>
                        <HStack spacing={1.5}>
                          <FiMail size={11} color="#A0AEC0" />
                          <Link href={`mailto:${contact.email}`} fontSize="12px" color="blue.500">{contact.email}</Link>
                        </HStack>
                      </Box>
                    </GridItem>
                  )}
                  <Field icon={FiPhone} label="Mobile" value={contact.mobile} />
                  <Field icon={FiPhone} label="Landline" value={contact.landline} />
                  <Field icon={FiBriefcase} label="Company" value={contact.company_name} />
                  <Field icon={FiBriefcase} label="Job Title" value={contact.job_title} />
                  {contact.birthday && (
                    <Field icon={FiCalendar} label="Birthday" value={fmtDate(contact.birthday)} />
                  )}
                  <Field icon={FiCalendar} label="Added" value={fmtDate(contact.created_at)} />
                </Grid>

                {/* Relationship notes (editable) */}
                <Divider />
                <Box>
                  <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
                    Notes — relationship, context, etc.
                  </Text>
                  <Textarea size="sm" rows={4} value={notesDraft} onChange={e => setNotesDraft(e.target.value)}
                    placeholder="Add a summary of our relationship or useful context…" />
                  <Flex justify="flex-end" mt={2}>
                    <Button size="xs" colorScheme="blue" onClick={saveNotes} isLoading={savingNotes}
                      isDisabled={notesDraft === (contact.notes || '')}>Save Notes</Button>
                  </Flex>
                </Box>

                {contact.description && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wide" mb={1}>Notes</Text>
                      <Box bg="gray.50" p={2} borderRadius="md" border="1px solid" borderColor="gray.100">
                        <Text fontSize="12px" color="gray.700" whiteSpace="pre-wrap">{contact.description}</Text>
                      </Box>
                    </Box>
                  </>
                )}

                {/* Addresses if present */}
                {(contact.address_line1 || contact.address_city || contact.address_postcode) && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wide" mb={1}>Address</Text>
                      <VStack align="stretch" spacing={0}>
                        {contact.address_line1 && <Text fontSize="12px" color="gray.700">{contact.address_line1}</Text>}
                        {contact.address_line2 && <Text fontSize="12px" color="gray.700">{contact.address_line2}</Text>}
                        {contact.address_city && <Text fontSize="12px" color="gray.700">{contact.address_city}</Text>}
                        {contact.address_county && <Text fontSize="12px" color="gray.700">{contact.address_county}</Text>}
                        {contact.address_postcode && <Text fontSize="12px" color="gray.700">{contact.address_postcode}</Text>}
                        {contact.address_country && <Text fontSize="12px" color="gray.500">{contact.address_country}</Text>}
                      </VStack>
                    </Box>
                  </>
                )}

              </VStack>
            </ModalBody>
          </>
        ) : (
          <Flex justify="center" align="center" h="120px">
            <Text fontSize="13px" color="gray.400">Contact not found</Text>
          </Flex>
        )}
      </ModalContent>
    </Modal>
  );
}
